import { Ref, ref } from "vue";
import { FieldError, HealthError } from "@/shared/models/HealthError.ts";
import { MESSAGES } from "@/shared/constants/messages.ts";
import { useNotify } from "@/shared/composables/useNotify.ts";
import { useJsonToClass } from "@/shared/composables/useJsonToClass.ts";
import { useI18n } from "vue-i18n";

type AsyncFunction<T> = (...args: any[]) => Promise<T>;

type ClassConstructor<T> = { new (data: any): T };

type UseRequest<T> = {
  fn: AsyncFunction<T>;
  modelClass?: ClassConstructor<T>;
  notifyOptions?: Partial<NotifyOptions>;
};

type NotifyOptions = {
  success: boolean;
  error: boolean;
  info: boolean;
  warning: boolean;
  errorMessage: string;
  successMessage: string;
  infoMessage: string | null;
  warningMessage: string | null;
};

const DEFAULT_NOTIFY_OPTIONS: NotifyOptions = {
  success: false,
  error: true,
  warning: false,
  info: false,
  errorMessage: null,
  successMessage: MESSAGES.successMessage,
  infoMessage: null,
  warningMessage: null,
};

const notifySuccess = (message: string) => {
  useNotify({
    message: message,
    type: "success",
  });
};

const notifyError = (message: string | string[]) => {
  Array.isArray(message)
    ? message.forEach((msg: string) =>
        useNotify({
          message: msg,
          type: "error",
        }),
      )
    : useNotify({
        message: message,
        type: "error",
      });
};

const getErrorMessage = (errorMessageCustom: string, response: HealthError): string | string[] => {
  if (response.fields.length) {
    return response.fields.map((field: FieldError) => field.userMessage);
  }

  return errorMessageCustom || response.userMessage || MESSAGES.errorMessage;
};

export const useRequest = <T>({
  fn,
  modelClass,
  notifyOptions = { ...DEFAULT_NOTIFY_OPTIONS },
}: UseRequest<T>) => {
  const data: Ref<T> = ref<T>(!!modelClass ? new modelClass() : undefined);
  const error: Ref<HealthError | null> = ref<HealthError | null>(null);
  const loading: Ref<boolean> = ref(false);
  const { t } = useI18n();

  function executeRequest<T>(...args: any[]): Promise<T> {
    loading.value = true;

    return new Promise((resolve, reject) => {
      fn(...args)
        .then((response: any) => {
          if (notifyOptions.success)
            notifySuccess(t(notifyOptions.successMessage || MESSAGES.successMessage));

          data.value = modelClass
            ? useJsonToClass(modelClass, response?.data as T)
            : (response?.data as T);

          resolve(response.data);
        })
        .catch(async (err) => {
          let errorObject = null;

          if (err.response && err.response.data instanceof Blob) {
            const errorBlob = err.response.data;

            const errorText = await errorBlob.text();

            errorObject = JSON.parse(errorText);
          }

          const healthError: string | string[] | HealthError = getErrorMessage(
            notifyOptions.errorMessage,
            HealthError.fromJson(errorObject || err?.response?.data),
          );

          notifyError(healthError);

          error.value = healthError;
          reject(healthError);
        })
        .finally(() => {
          loading.value = false;
        });
    });
  }

  return {
    data,
    error,
    loading,
    request: executeRequest,
  };
};
